<template>
  <modal-cmp
    class="NutritionModalCmp"
    :data-name="dataName"
    :data-has-inner-scroll="true"
    :data-has-close-button="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div
      ref="$dialog"
      class="nutrition-dialog"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-label="dataTitle"
    >
      <button
        class="nutrition-dialog__close"
        type="button"
        :aria-label="dataCloseLabel"
        @click="close"
      >
        <span class="sr-only">{{ dataCloseLabel }}</span>
      </button>

      <div class="nutrition-dialog__body">
        <div class="nutrition-dialog__frame">
          <iframe
            v-if="isOpen"
            :src="dataWidgetUrl"
            :title="dataIframeTitle"
            loading="lazy"
            referrerpolicy="no-referrer"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            allow=""
          ></iframe>
        </div>

        <p v-if="dataFallbackLabel" class="nutrition-dialog__fallback">
          <a :href="dataWidgetUrl" target="_blank" rel="noopener noreferrer">
            {{ dataFallbackLabel }}
          </a>
        </p>
      </div>
    </div>
  </modal-cmp>
</template>

<script>
import ModalCmp from '@/components/modal-cmp';

const FOCUSABLE =
  'a[href], button:not([disabled]), iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default {
  name: 'NutritionModalCmp',
  components: { ModalCmp },
  props: {
    dataWidgetUrl: { type: String, required: true },
    dataTitle: { type: String, default: '' },
    dataIframeTitle: { type: String, default: '' },
    dataFallbackLabel: { type: String, default: '' },
    dataCloseLabel: { type: String, default: 'Close' },
    dataName: { type: String, default: 'nutrition-modal' },
  },
  data() {
    return {
      isOpen: false,
      lastFocusedElement: null,
    };
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    open() {
      this.lastFocusedElement = document.activeElement;
      this.isOpen = true;
      this.$modal.show(this.dataName);
    },
    close() {
      this.$modal.hide(this.dataName);
    },
    onOpened() {
      // Escape and Tab are handled on document: right after opening, focus can
      // still be on body, and a listener scoped to the dialog would miss them.
      document.addEventListener('keydown', this.onKeydown);
      if (this.$refs.$dialog) {
        this.$refs.$dialog.focus();
      }
      this.$emit('opened');
    },
    onClosed() {
      document.removeEventListener('keydown', this.onKeydown);
      this.isOpen = false;
      if (this.lastFocusedElement && this.lastFocusedElement.focus) {
        this.lastFocusedElement.focus();
      }
      this.lastFocusedElement = null;
      this.$emit('closed');
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = Array.from(this.$el.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el.tagName === 'IFRAME'
      );
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        first.focus();
        event.preventDefault();
      } else if (!this.$el.contains(document.activeElement)) {
        first.focus();
        event.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin on-custom-hover() {
  @media (hover: hover),
    (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    &:hover {
      @content;
    }
  }

  @media (hover: none) {
    &:active {
      @content;
    }
  }
}

$nutrition-widget-width: 570px;
$nutrition-panel-padding-x: 55px;
$nutrition-panel-width: $nutrition-widget-width + $nutrition-panel-padding-x * 2;
$nutrition-close-size: 58px;
$nutrition-close-clearance: 96px;
$nutrition-close-glyph: 21.33px;

.NutritionModalCmp {
  // modal-cmp sizes its box for text (col-8 at lg): too narrow for a fixed-width
  // embed, so from md up the box is as wide as the widget instead.
  ::v-deep .modal-container .modal-box {
    @include media-breakpoint-up(md) {
      width: 100% !important;
      max-width: $nutrition-panel-width !important;
    }
  }

  .nutrition-dialog {
    position: relative;
    display: flex;
    flex-direction: column;
    border-top: 0 !important;
    overflow: hidden !important;

    &:focus {
      outline: none;
    }
  }

  .nutrition-dialog__body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: $nutrition-close-clearance 0 24px;

    @include media-breakpoint-up(md) {
      padding: $nutrition-close-clearance $nutrition-panel-padding-x 40px;
    }
  }

  // The embedded page has no viewport meta and lays out at a fixed width:
  // we give it room and let the container scroll rather than clipping it.
  .nutrition-dialog__frame {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;

    iframe {
      display: block;
      width: 100%;
      min-width: $nutrition-widget-width;
      height: 500px;
      border: 0;
    }
  }

  .nutrition-dialog__fallback {
    margin: 16px 0 0;
    padding: 0 24px;

    @include media-breakpoint-up(md) {
      padding: 0 40px;
    }

    a {
      color: $color-blue;
      text-decoration: underline;
    }
  }

  .nutrition-dialog__close {
    position: absolute;
    top: 24px;
    right: 24px;
    z-index: 2;
    width: $nutrition-close-size;
    height: $nutrition-close-size;
    padding: 0;
    border: 2px solid $color-blue;
    border-radius: 50%;
    background: $color-white;
    cursor: pointer;
    transition: border-color 0.3s ease;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      width: $nutrition-close-glyph;
      height: 2px;
      margin: -1px 0 0 (-$nutrition-close-glyph / 2);
      background: $color-blue;
      transition: background-color 0.3s ease;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }

    @include on-custom-hover() {
      border-color: $color-light-blue;

      &::before,
      &::after {
        background: $color-light-blue;
      }
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
  }
}
</style>
